import { useWatch, type Control } from 'react-hook-form'
import { CampoDeTextoLongo } from '../../../components/campos'
import { CAMPOS_DO_PLANO } from '../../../lib/glossario'
import type { CampoDoPlano } from '../../../types/gmud'
import { LIMITE_DO_PLANO, type FormularioNovaGmud, type PropsDosCampos } from './esquema'
import { ARTEFATOS_EXIGIDOS } from './pendencias'

interface Props extends PropsDosCampos {
  control: Control<FormularioNovaGmud>
}

interface PropsDeCampo extends Props {
  campo: CampoDoPlano
  rotulo: string
  ajuda: string
}

const TETO = LIMITE_DO_PLANO.toLocaleString('pt-BR')

/**
 * Um artefato do plano, com o contador embaixo.
 *
 * `useWatch` por campo, e não um `watch()` no formulário inteiro: assim digitar no passo a passo
 * da implantação não redesenha os outros sete campos de texto a cada tecla. Com textos de milhares
 * de caracteres — que é o tamanho normal de um procedimento de implantação — a diferença aparece
 * como atraso entre a tecla e a letra.
 *
 * O contador não é `aria-live`: anunciar o número a cada tecla atropelaria a digitação de quem usa
 * leitor de tela, e o teto de 20.000 caracteres só é alcançado colando um documento inteiro. Ele
 * está ali para orientar, não para avisar.
 *
 * @param props o campo, seus textos e o controle do formulário
 * @returns o campo de texto longo com o contador
 */
function CampoComContador({ campo, rotulo, ajuda, control, register, errors }: PropsDeCampo) {
  const texto = useWatch({ control, name: campo }) ?? ''
  const exigido = ARTEFATOS_EXIGIDOS.includes(campo)

  return (
    <div>
      <CampoDeTextoLongo rotulo={rotulo} ajuda={ajuda} erro={errors[campo]?.message} rows={4} {...register(campo)} />
      <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2 text-xs">
        <span className={exigido ? 'font-medium text-laranja-700' : 'text-slate-500'}>
          {exigido ? 'Exigido para enviar ao comitê' : 'Não é exigido pelo comitê'}
        </span>
        <span className="text-slate-500">
          {texto.length.toLocaleString('pt-BR')} de {TETO} caracteres
        </span>
      </div>
    </div>
  )
}

/**
 * Os oito artefatos do plano, na ordem do ANEXO XXI.
 *
 * A lista vem do glossário e não está escrita aqui: o rótulo que o usuário lê e a pergunta que
 * cada campo responde são texto de interface, e texto de interface mora num lugar só. Acrescentar
 * um artefato passa a ser uma entrada em `CAMPOS_DO_PLANO`.
 *
 * @param props registro do formulário, erros e o controle (para o contador)
 * @returns os campos do plano
 */
export function CamposDoPlano({ register, errors, control }: Props) {
  return (
    <div className="space-y-5">
      {CAMPOS_DO_PLANO.map((campo) => (
        <CampoComContador
          key={campo.campo}
          campo={campo.campo}
          rotulo={campo.rotulo}
          ajuda={campo.ajuda}
          register={register}
          errors={errors}
          control={control}
        />
      ))}
    </div>
  )
}
